import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, ShieldAlert, RefreshCw, Check, X, Download, Loader2,
  // P0-12 Sprint 2 — Witness pill icons. ExternalLink for the clickable
  // verify-at-source link; Anchor as the witness pill's lead icon
  // (visual metaphor: "the chain is anchored to an external mirror").
  Anchor, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { Button } from '../index';
import { chainState, countBreakKinds } from '../../../utils/audit/verifyScope';

/**
 * AuditHashIntegrity — verify the trust event audit log's hash chain
 * AND download a tamper-evident audit certificate.
 *
 * <p>P0-12 surface for the SHA-256 hash-chained PolicyAuditEvent
 * substrate built in the backend. Two actions:</p>
 *
 * <ul>
 *   <li><strong>Verify integrity</strong> — calls the verifier and
 *       renders the verdict inline. The fastest way for a customer to
 *       prove "no event in this window was tampered with."</li>
 *   <li><strong>Download certificate</strong> — calls the new
 *       /api/v1/policy/audit/certificate endpoint and triggers a JSON
 *       download. The certificate is self-hashed; auditors verify by
 *       re-canonicalising the JSON minus certificateHash and comparing.</li>
 * </ul>
 *
 * <h3>Backend contract</h3>
 * <pre>
 *   verifyFn(timeRange)              → { ok, valid, eventCount?, brokenAt?, error? }
 *   downloadFn({ hours, agentId? })  → { ok, certificateId?, certificateHash?, valid?, error? }
 * </pre>
 *
 * <h3>Props</h3>
 * <ul>
 *   <li><code>verifyFn</code> — required when "Verify integrity" should
 *       be enabled. Called with the active timeRange.</li>
 *   <li><code>downloadFn</code> — optional. When provided, the
 *       "Download certificate" button is rendered.</li>
 *   <li><code>timeRange</code> — '24h' | '7d' | '30d' | '90d'. Default '30d'.</li>
 *   <li><code>agentId</code> — optional UUID. When supplied, the
 *       certificate is scoped to this agent's chain (DV-2 Phase B).
 *       Use on Agent Detail pages.</li>
 *   <li><code>autoVerify</code> — default false. When true, mount-time
 *       verification runs automatically. Set true for the page-header
 *       surface in Trust Center V2 (W148: always-visible status).</li>
 *   <li><code>compact</code> — default false. When true, renders the
 *       compact header-bar variant suitable for the Trust Center page
 *       header (single line with status pill + buttons).</li>
 *   <li><code>witnessFn</code> — optional. When provided, called on
 *       mount (and on Verify) to fetch the most-recent external-mirror
 *       witness anchor. The witness pill is rendered next to the
 *       chain-integrity pill, surfacing the backend + status + opaque
 *       ID + clickable witness URL. P0-12 Sprint 2.</li>
 * </ul>
 *
 * <h3>Tones</h3>
 * <p>Always reach for the existing v2 design tokens (bg-v2-success-soft,
 * text-v2-danger-strong, etc.). Never hard-code colors.</p>
 */
export function AuditHashIntegrity({
  verifyFn,
  downloadFn,
  witnessFn,
  timeRange = '30d',
  agentId = null,
  initialState = 'idle',
  autoVerify = false,
  compact = false,
}) {
  const [state, setState] = useState(initialState);  // idle | verifying | valid | broken | error
  const [result, setResult] = useState(null);
  const [lastVerifiedAt, setLastVerifiedAt] = useState(null);
  const [downloadState, setDownloadState] = useState('idle');     // idle | downloading | done | error
  const [downloadResult, setDownloadResult] = useState(null);
  // P0-12 Sprint 2 — Witness state. `witness` is the latest
  // AuditWitnessEntry row (or null when no anchor exists / fetch
  // failed quietly). `witnessState` discriminates so the UI can
  // distinguish "not loaded yet" from "loaded, no anchor".
  const [witness, setWitness] = useState(null);
  const [witnessState, setWitnessState] = useState('idle'); // idle | loading | loaded | empty | error

  // Map the timeRange string to a numeric hours value the backend accepts.
  // Centralised here so the verify and download calls always agree.
  const timeRangeHours = (() => {
    switch (timeRange) {
      case '24h': return 24;
      case '7d':  return 24 * 7;
      case '30d': return 24 * 30;
      case '90d': return 24 * 90;
      default:    return 24 * 30;
    }
  })();

  // P0-12 Sprint 2 — Load the latest witness anchor on mount (and on
  // re-mount when scope changes). The fetch is best-effort: a failure
  // leaves the pill in 'error' state but never blocks the rest of the
  // surface. The pill is also OMITTED entirely when no witnessFn is
  // supplied (legacy callers stay untouched).
  const loadWitness = useCallback(async () => {
    if (typeof witnessFn !== 'function') return;
    setWitnessState('loading');
    try {
      const r = await witnessFn();
      if (r?.ok === false) {
        setWitnessState('error');
        setWitness(null);
        return;
      }
      const entry = r?.entry ?? r;
      if (!entry || !entry.id) {
        setWitnessState('empty');
        setWitness(null);
        return;
      }
      setWitness(entry);
      setWitnessState('loaded');
    } catch (e) {
      setWitnessState('error');
      setWitness(null);
    }
  }, [witnessFn]);

  useEffect(() => {
    if (typeof witnessFn === 'function') loadWitness();
    // eslint-disable-next-line
  }, [witnessFn]);

  const handleVerify = useCallback(async () => {
    if (typeof verifyFn !== 'function') {
      setState('error');
      setResult({ error: 'No verification function configured.' });
      return;
    }
    setState('verifying');
    setResult(null);
    try {
      // P0-12 Day 3 — always pass a structured config so the verifier
      // can scope to (hours, agentId) for DV-2 Phase B agent-scoped
      // chain verification. The legacy `timeRange` string is included
      // for callers that still want to do the hours-mapping themselves
      // (e.g. TrustTimelineTab's wrapper which reads `.timeRange`).
      const cfg = { hours: timeRangeHours, agentId: agentId || undefined, timeRange };
      const r = await verifyFn(cfg);
      if (r?.ok === false) {
        setState('error');
        setResult({ error: r.error || 'Verification request failed.' });
        return;
      }
      const data = r?.data || r;
      const eventCount = data?.eventCount ?? data?.eventsChecked ?? data?.count ?? 0;
      const broken = data?.valid === false || data?.brokenAt
        || (data?.breaks && data.breaks.length > 0)
        || (data?.chainBreaks && data.chainBreaks.length > 0);
      if (broken) {
        /* Four outcomes, not two, and not derived here. A chain can be invalid because
         * records were altered, because they were appended out of order, or because some
         * of them can no longer be checked at all — all invalid, only the first a security
         * event. Collapsing the first two is what put "Hash chain BROKEN" in front of an
         * operator for a race condition on 2026-10-14; this component then derived that
         * distinction itself while the Audit Trail banner derived it separately, so when
         * the third outcome appeared only one of them would have learned about it. One
         * owner now: chainState. */
        setState(chainState(data));
        setResult({
          eventCount,
          brokenAt: data?.brokenAt
            || (data?.breaks && data.breaks[0]?.message)
            || (data?.chainBreaks && data.chainBreaks[0])
            || 'unknown event',
          contentUnaltered: data?.contentUnaltered === true,
          unverifiableCount: countBreakKinds(data).unverifiable,
          orderingCount: countBreakKinds(data).other,
          scopeLabel: data?.scopeLabel || null,
          breakCount: (data?.breaks || data?.chainBreaks || []).length,
        });
      } else {
        setState('valid');
        setResult({ eventCount, scopeLabel: data?.scopeLabel || null });
      }
      setLastVerifiedAt(new Date());
    } catch (e) {
      setState('error');
      setResult({ error: e?.message || String(e) });
    }
  }, [verifyFn, timeRange, timeRangeHours, agentId]);

  // Auto-verify on mount when requested. Re-runs if the time range or
  // agent scope changes — the verification result is only valid for the
  // window it was computed against.
  useEffect(() => {
    if (autoVerify && typeof verifyFn === 'function') {
      handleVerify();
    }
    // We deliberately depend on the dynamic inputs; handleVerify is
    // memoised on the same inputs.
    // eslint-disable-next-line
  }, [autoVerify, verifyFn, timeRange, agentId]);

  const handleDownload = async () => {
    if (typeof downloadFn !== 'function') return;
    setDownloadState('downloading');
    setDownloadResult(null);
    try {
      const r = await downloadFn({ hours: timeRangeHours, agentId });
      if (r?.ok === false) {
        setDownloadState('error');
        setDownloadResult({ error: r.error || 'Download failed.' });
        return;
      }
      setDownloadState('done');
      setDownloadResult({
        certificateId: r?.certificateId,
        certificateHash: r?.certificateHash,
        valid: r?.valid,
        // P0-1 — Surface the new framework binding transition evidence
        // counts so the operator sees, in the success pill, exactly
        // what the downloaded certificate covers. transitionsCount is
        // the number of state-change rows; bindingChainsCount is the
        // number of distinct framework bindings those transitions
        // touched. Both are derived from the certificate body the
        // downloader just parsed; if the body wasn't JSON-parseable
        // they default to 0 (graceful).
        transitionsCount: r?.transitionsCount ?? 0,
        bindingChainsCount: r?.bindingChainsCount ?? 0,
        totalEvents: r?.totalEvents ?? null,
      });
      // Reset the "done" pill after 6s so the button returns to normal.
      setTimeout(() => setDownloadState('idle'), 6000);
    } catch (e) {
      setDownloadState('error');
      setDownloadResult({ error: e?.message || String(e) });
    }
  };

  // ── Render variants ──────────────────────────────────────────
  if (compact) {
    return renderCompact({
      state, result, lastVerifiedAt,
      downloadState, downloadResult,
      verifyFn, downloadFn, witnessFn, agentId, timeRange,
      witness, witnessState, onReloadWitness: loadWitness,
      onVerify: handleVerify,
      onDownload: handleDownload,
    });
  }

  // Full / card variant — the original layout from the Timeline tab.
  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-4 flex items-start gap-3">
      <div className={`shrink-0 w-10 h-10 rounded-v2-md flex items-center justify-center ${
        state === 'valid'    ? 'bg-v2-success-soft text-v2-success-strong' :
        state === 'broken'   ? 'bg-v2-danger-soft text-v2-danger-strong' :
        state === 'error'    ? 'bg-v2-warning-soft text-v2-warning-strong' :
                               'bg-v2-action-soft text-v2-action-strong'
      }`}>
        {state === 'valid'  ? <ShieldCheck size={20} /> :
         state === 'broken' ? <ShieldAlert size={20} /> :
                              <ShieldCheck size={20} />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3 mb-1 flex-wrap">
          <h3 className="text-[13px] font-semibold text-v2-ink m-0">
            Audit hash chain integrity
            {agentId && (
              <span className="ml-2 text-[11px] font-normal text-v2-ink-secondary">
                — scoped to this agent
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary" size="sm"
              iconLeft={state === 'verifying'
                ? <RefreshCw size={13} className="animate-spin" />
                : <ShieldCheck size={13} />}
              disabled={state === 'verifying' || typeof verifyFn !== 'function'}
              onClick={handleVerify}
            >
              {state === 'verifying' ? 'Verifying…' : 'Verify integrity'}
            </Button>
            {typeof downloadFn === 'function' && (
              <Button
                variant="primary" size="sm"
                iconLeft={downloadState === 'downloading'
                  ? <Loader2 size={13} className="animate-spin" />
                  : downloadState === 'done'
                    ? <Check size={13} />
                    : <Download size={13} />}
                disabled={downloadState === 'downloading'}
                onClick={handleDownload}
              >
                {downloadState === 'downloading' ? 'Generating…'
                 : downloadState === 'done'        ? 'Downloaded'
                 :                                   'Download certificate'}
              </Button>
            )}
          </div>
        </div>

        <p className="text-[12px] text-v2-ink-secondary m-0">
          Every audit event is sealed with a SHA-256 hash of the previous event. Verification
          walks the chain end-to-end and surfaces the first break (if any). The downloadable
          certificate is a self-hashed tamper-evident bundle suitable for handing to an auditor
          or notified body.
        </p>

        {state === 'valid' && (
          <div className="mt-3 flex items-center gap-2 text-[12px] text-v2-success-strong">
            <Check size={14} />
            <span>
              {/* Not "no tampering detected" — that claims more than the walk establishes
                  and invites an auditor to read it as a guarantee. State what was checked:
                  every record matches its own contents and its position in the chain. */}
              {result?.scopeLabel || 'Chain'} verified — <strong>{result?.eventCount ?? '—'}</strong> event{result?.eventCount === 1 ? '' : 's'} in window;
              each one matches its own contents and its place in the chain.
              {lastVerifiedAt && (
                <span className="ml-2 text-v2-ink-secondary">
                  · {lastVerifiedAt.toLocaleTimeString()}
                </span>
              )}
            </span>
          </div>
        )}
        {state === 'outOfOrder' && (
          <div className="mt-3 bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-warning-strong">
            <div className="flex items-center gap-2 font-semibold">
              <AlertTriangle size={14} /> {result?.scopeLabel || 'Chain'} out of order — content intact
            </div>
            <p className="m-0 mt-1">
              {result?.breakCount ? `${result.breakCount} ordering break(s) across ` : ''}
              {result?.eventCount ? `${result.eventCount} events. ` : ''}
              Every record still matches its own contents, so nothing was altered. What is
              wrong is the order they were linked in — which is a defect in how they were
              written, not evidence of interference. Re-linking the chain is safe here.
            </p>
          </div>
        )}
        {state === 'unverifiable' && (
          <div className="mt-3 bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-warning-strong">
            <div className="flex items-center gap-2 font-semibold">
              <AlertTriangle size={14} /> {result?.scopeLabel || 'Chain'} — {result?.unverifiableCount ?? 'some'} earlier record{result?.unverifiableCount === 1 ? '' : 's'} can no longer be checked
            </div>
            <p className="m-0 mt-1">
              {result?.eventCount ? `${result.eventCount} events scanned. ` : ''}
              These records were sealed by an earlier version of this system, and the
              information needed to re-check their seals was not kept. Nothing indicates they
              were changed, and nothing here can confirm they were not — the honest status is
              unchecked. Everything sealed since then verifies normally.
              {result?.orderingCount ? ` ${result.orderingCount} further record(s) are also out of order.` : ''}
            </p>
            <p className="m-0 mt-1">
              Putting these back into a checkable state is a deliberate, recorded action — it
              is not offered as an automatic repair, because re-sealing them would present
              them as verified when they have not been.
            </p>
          </div>
        )}
        {state === 'broken' && (
          <div className="mt-3 bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-danger-strong">
            <div className="flex items-center gap-2 font-semibold">
              <X size={14} /> Hash chain BROKEN
            </div>
            <p className="m-0 mt-1">
              {result?.eventCount ? `${result.eventCount} events scanned; ` : ''}
              first inconsistency at <code className="font-mono-v2">{result?.brokenAt || 'unknown event'}</code>.
              Treat the audit trail as compromised and investigate immediately.
            </p>
          </div>
        )}
        {state === 'error' && (
          <div className="mt-3 bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-warning-strong">
            Verification could not complete: {result?.error || 'unknown error'}.
          </div>
        )}

        {downloadState === 'done' && downloadResult?.certificateHash && (
          <div className="mt-3 bg-v2-success-soft border border-v2-success/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-success-strong">
            <div className="font-semibold">Certificate saved.</div>
            <p className="m-0 mt-1 font-mono-v2 break-all">
              ID: {downloadResult.certificateId}
              <br />
              SHA-256: {downloadResult.certificateHash}
            </p>
            {/* P0-1 — Evidence coverage summary. Auditors care about
                what's IN the certificate; surfacing the counts here
                saves them from opening the JSON to find out. */}
            {(downloadResult.totalEvents != null
              || downloadResult.transitionsCount > 0) && (
              <p className="m-0 mt-2 text-[11px] font-normal opacity-90">
                Covers{' '}
                {downloadResult.totalEvents != null && (
                  <strong>{downloadResult.totalEvents} event{downloadResult.totalEvents === 1 ? '' : 's'}</strong>
                )}
                {downloadResult.totalEvents != null
                  && downloadResult.transitionsCount > 0 && ' · '}
                {downloadResult.transitionsCount > 0 && (
                  <>
                    <strong>{downloadResult.transitionsCount}</strong>{' '}
                    framework binding transition{downloadResult.transitionsCount === 1 ? '' : 's'}
                    {downloadResult.bindingChainsCount > 0 && (
                      <> across <strong>{downloadResult.bindingChainsCount}</strong> binding{downloadResult.bindingChainsCount === 1 ? '' : 's'}</>
                    )}
                  </>
                )}.
              </p>
            )}
          </div>
        )}
        {downloadState === 'error' && (
          <div className="mt-3 bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-warning-strong">
            Certificate download failed: {downloadResult?.error || 'unknown error'}.
          </div>
        )}

        {/* P0-12 Sprint 2 — External-mirror witness row. Rendered ONLY
            when a witnessFn was supplied (legacy callers stay
            untouched). The row sits below the certificate-download
            block because it's evidence ABOUT the cert, not an action
            the user takes. */}
        {typeof witnessFn === 'function' && (
          <div className="mt-3">
            {renderWitnessPill({ witness, witnessState, onReload: loadWitness, layout: 'block' })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Compact one-line variant for the Trust Center V2 page header.
 * Shows a tiny status pill + Verify and Download buttons; click expands
 * to the full card layout if more detail is needed (handled by the host).
 */
function renderCompact({
  state, result, lastVerifiedAt,
  downloadState, downloadResult,
  verifyFn, downloadFn, witnessFn, agentId, timeRange,
  witness, witnessState, onReloadWitness,
  onVerify, onDownload,
}) {
  const pillTone =
    state === 'valid'  ? 'bg-v2-success-soft text-v2-success-strong border-v2-success/30' :
    state === 'broken' ? 'bg-v2-danger-soft text-v2-danger-strong border-v2-danger/30' :
    state === 'outOfOrder' ? 'bg-v2-warning-soft text-v2-warning-strong border-v2-warning/30' :
    state === 'unverifiable' ? 'bg-v2-warning-soft text-v2-warning-strong border-v2-warning/30' :
    state === 'error'  ? 'bg-v2-warning-soft text-v2-warning-strong border-v2-warning/30' :
                         'bg-v2-action-soft text-v2-action-strong border-v2-action/30';
  const pillLabel =
    state === 'valid'     ? 'Chain verified' :
    state === 'broken'    ? 'Records altered' :
    state === 'outOfOrder' ? 'Out of order' :
    /* Not "Records altered" and not "Chain verified". Neither is true, and an operator
     * surface that must choose between two wrong words should be given the right one. */
    state === 'unverifiable' ? 'Partly unchecked' :
    state === 'error'     ? 'Verify failed' :
    state === 'verifying' ? 'Verifying…' :
                            'Not verified';
  const PillIcon =
    state === 'valid'      ? ShieldCheck :
    state === 'broken'     ? ShieldAlert :
    /* Out of order is a writer defect with the evidence intact. It should read as
     * something to fix, not as the alarm reserved for an altered record. */
    state === 'outOfOrder' ? AlertTriangle :
    state === 'unverifiable' ? AlertTriangle :
    state === 'verifying'  ? RefreshCw :
                             ShieldCheck;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-v2-sm text-[11.5px] font-semibold border ${pillTone}`}
        title={agentId
          ? `Audit chain integrity for this agent · window: ${timeRange}`
          : `Audit chain integrity for the tenant · window: ${timeRange}`}
      >
        <PillIcon size={13} className={state === 'verifying' ? 'animate-spin' : ''} />
        {pillLabel}
        {state === 'valid' && typeof result?.eventCount === 'number' && (
          <span className="ml-1 font-normal opacity-75">· {result.eventCount} events</span>
        )}
      </span>

      {lastVerifiedAt && state === 'valid' && (
        <span className="text-[11px] text-v2-ink-secondary" title={lastVerifiedAt.toISOString()}>
          Verified {formatRelative(lastVerifiedAt)}
        </span>
      )}

      {/* P0-12 Sprint 2 — Compact witness pill alongside the
          chain-integrity pill. Same row, sits before the action
          buttons so the operator can see both signals at a glance. */}
      {typeof witnessFn === 'function' && (
        renderWitnessPill({ witness, witnessState, onReload: onReloadWitness, layout: 'inline' })
      )}

      <div className="flex items-center gap-2 ml-auto">
        <Button
          variant="ghost" size="sm"
          iconLeft={state === 'verifying'
            ? <RefreshCw size={12} className="animate-spin" />
            : <ShieldCheck size={12} />}
          disabled={state === 'verifying' || typeof verifyFn !== 'function'}
          onClick={onVerify}
        >
          {state === 'verifying' ? 'Verifying…' : 'Verify'}
        </Button>
        {typeof downloadFn === 'function' && (
          <Button
            variant="secondary" size="sm"
            iconLeft={downloadState === 'downloading'
              ? <Loader2 size={12} className="animate-spin" />
              : downloadState === 'done'
                ? <Check size={12} />
                : <Download size={12} />}
            disabled={downloadState === 'downloading'}
            onClick={onDownload}
            title={agentId
              ? 'Download tamper-evident audit certificate scoped to this agent'
              : 'Download tamper-evident audit certificate for the tenant'}
          >
            {downloadState === 'downloading' ? 'Generating…'
             : downloadState === 'done'        ? 'Saved'
             :                                   'Certificate'}
          </Button>
        )}
      </div>

      {/* Toast-style detail line below the compact bar when there's
          something worth surfacing (chain broken, error, downloaded hash). */}
      {state === 'broken' && (
        <div className="w-full mt-2 text-[11.5px] text-v2-danger-strong bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm px-3 py-1.5">
          Chain inconsistency at <code className="font-mono-v2">{result?.brokenAt}</code> —
          investigate immediately.
        </div>
      )}
      {state === 'error' && (
        <div className="w-full mt-2 text-[11.5px] text-v2-warning-strong bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-3 py-1.5">
          Verification could not complete: {result?.error}.
        </div>
      )}
      {downloadState === 'error' && (
        <div className="w-full mt-2 text-[11.5px] text-v2-warning-strong bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-3 py-1.5">
          Certificate download failed: {downloadResult?.error || 'unknown error'}.
        </div>
      )}
      {downloadState === 'done' && downloadResult?.certificateHash && (
        <div className="w-full mt-2 text-[11.5px] text-v2-success-strong bg-v2-success-soft border border-v2-success/30 rounded-v2-sm px-3 py-1.5">
          Certificate saved · <code className="font-mono-v2">{downloadResult.certificateId}</code>
          <span className="ml-2 opacity-75">SHA-256: {downloadResult.certificateHash.slice(0, 16)}…</span>
          {/* P0-1 — Same evidence summary as the full card variant, in
              a single-line form suitable for the page-header. */}
          {downloadResult.transitionsCount > 0 && (
            <span className="ml-2 opacity-90">
              · <strong>{downloadResult.transitionsCount}</strong> binding transition{downloadResult.transitionsCount === 1 ? '' : 's'}
              {downloadResult.bindingChainsCount > 0 && (
                <> ({downloadResult.bindingChainsCount} binding{downloadResult.bindingChainsCount === 1 ? '' : 's'})</>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * P0-12 Sprint 2 — Render the external-mirror witness pill.
 *
 * <p>Two layouts:</p>
 *
 * <ul>
 *   <li><code>'inline'</code> — single-pill form for the compact
 *       page-header bar. Pill + (truncated) witness ID + status
 *       discriminator. Click the pill to open the witness URL in
 *       a new tab when present.</li>
 *   <li><code>'block'</code> — full-card form for the audit-trail
 *       card. Two-line render with backend + status + ID on top,
 *       "anchored at" relative time + clickable witness URL below.</li>
 * </ul>
 *
 * <p>States:</p>
 *
 * <ul>
 *   <li><code>idle</code> / <code>loading</code> — neutral "loading"
 *       pill so the layout doesn't shift when the data arrives.</li>
 *   <li><code>empty</code> — "Not witnessed yet" muted pill. Common
 *       on new tenants before the daily scheduler runs.</li>
 *   <li><code>error</code> — warning pill with a Reload action.</li>
 *   <li><code>loaded</code> — pill colour driven by
 *       <code>witness.status</code> (OK / FAILED / UNAVAILABLE).</li>
 * </ul>
 */
function renderWitnessPill({ witness, witnessState, onReload, layout = 'inline' }) {
  if (witnessState === 'idle' || witnessState === 'loading') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-v2-sm text-[11.5px] font-semibold border bg-v2-action-soft text-v2-action-strong border-v2-action/30"
        title="Loading external-mirror witness status"
      >
        <RefreshCw size={12} className="animate-spin" />
        Witness: loading…
      </span>
    );
  }
  if (witnessState === 'empty') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-v2-sm text-[11.5px] font-semibold border bg-v2-surface text-v2-ink-secondary border-v2-border-subtle"
        title="No external-mirror anchor has been recorded for this tenant yet."
      >
        <Anchor size={12} />
        Not witnessed yet
      </span>
    );
  }
  if (witnessState === 'error') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-v2-sm text-[11.5px] font-semibold border bg-v2-warning-soft text-v2-warning-strong border-v2-warning/30"
        title="Could not load witness status. Click to retry."
        role="button"
        tabIndex={0}
        onClick={onReload}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onReload(); }}
      >
        <AlertTriangle size={12} />
        Witness: load failed
        <span className="ml-1 underline">retry</span>
      </span>
    );
  }
  // witnessState === 'loaded' — render based on row status
  const status = (witness?.status || 'UNAVAILABLE').toUpperCase();
  const isOk = status === 'OK';
  const isUnavailable = status === 'UNAVAILABLE';
  const tone =
    isOk          ? 'bg-v2-success-soft text-v2-success-strong border-v2-success/30' :
    isUnavailable ? 'bg-v2-surface text-v2-ink-secondary border-v2-border-subtle' :
                    'bg-v2-warning-soft text-v2-warning-strong border-v2-warning/30';
  const label =
    isOk          ? 'Witnessed' :
    isUnavailable ? 'Witness unavailable' :
                    'Witness failed';
  const backend = witness?.witnessBackend || witness?.backend || '—';
  const witnessId = witness?.witnessId || '';
  const witnessUrl = witness?.witnessUrl || '';
  const anchoredAt = witness?.anchoredAt
    ? new Date(witness.anchoredAt)
    : null;
  const idDisplay = witnessId
    ? (witnessId.length > 12 ? `${witnessId.slice(0, 8)}…${witnessId.slice(-4)}` : witnessId)
    : null;

  if (layout === 'inline') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-v2-sm text-[11.5px] font-semibold border ${tone}`}
        title={
          `External-mirror witness · backend=${backend} · status=${status}`
          + (anchoredAt ? ` · anchored ${anchoredAt.toISOString()}` : '')
          + (witness?.failureReason ? `\n${witness.failureReason}` : '')
        }
      >
        <Anchor size={12} />
        {label}
        {anchoredAt && (
          <span className="ml-1 font-normal opacity-75">
            · {formatRelative(anchoredAt)}
          </span>
        )}
        {witnessUrl && (
          <a
            href={witnessUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-1.5 inline-flex items-center gap-0.5 underline hover:no-underline"
            onClick={(e) => e.stopPropagation()}
            title="Open at the witness backend (verify independently)"
          >
            verify <ExternalLink size={11} />
          </a>
        )}
      </span>
    );
  }
  // block layout — full card row
  return (
    <div className={`flex items-start gap-3 border rounded-v2-sm px-3 py-2 text-[12px] ${tone}`}>
      <Anchor size={14} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold">
          {label}
          <span className="ml-2 font-normal opacity-80">· backend: {backend}</span>
          {anchoredAt && (
            <span className="ml-2 font-normal opacity-80">· {formatRelative(anchoredAt)}</span>
          )}
        </div>
        <div className="mt-0.5 font-mono-v2 break-all opacity-90">
          {idDisplay && <>id: {idDisplay}</>}
          {witness?.headHash && (
            <span className="ml-2 opacity-80">head: {witness.headHash.slice(0, 12)}…</span>
          )}
        </div>
        {witnessUrl && (
          <a
            href={witnessUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 underline hover:no-underline"
            title="Open at the witness backend (verify independently)"
          >
            Verify at source <ExternalLink size={11} />
          </a>
        )}
        {!isOk && witness?.failureReason && (
          <div className="mt-1 opacity-90">
            Reason: {witness.failureReason}
          </div>
        )}
      </div>
    </div>
  );
}

/** Format a Date relative to now in a CISO-friendly way: "just now", "3m ago", "1h ago", "2d ago". */
function formatRelative(d) {
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 5)      return 'just now';
  if (diff < 60)     return `${diff}s ago`;
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default AuditHashIntegrity;
